/**
 * The live facts the Alerts table's Where column reads (`alert-where.ts`),
 * from the same queries and the same presence the Fleet and Devices
 * screens read, so that the three can never name two floors for one
 * device.
 *
 * No new reads: every query here is one the shell already keeps current
 * (`useLiveData` writes robots, lifts, doors and tasks into these very
 * keys), and the door registry is the one `useDoors` reads.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRobots, useTasks } from '../../api/hooks';
import { useDoors } from '../../api/hooks/useDoors';
import { useLifts } from '../../api/hooks/useLifts';
import { doorsRegistryQueryOptions } from '../../api/query-options';
import { robotReceiptKey } from '../../api/robot-receipts';
import { scopeByActiveSite } from '../../api/scope';
import type { SiteId } from '../../api/v1';
import { useCanSeeSimulated } from '../../auth/useCanSeeSimulated';
import { splitRobotRef } from '../../state/locate';
import { isSimulatedFleet } from '../../state/simulated-fleet';
import { useSafeSiteId } from '../../state/useActiveSite';
import { useLiftCarViews } from '../Devices/useLiftColumnState';
import { useRobotPresence } from '../Map/useRobotPresence';
import { robotWhereFloor, type AlertWhereFacts } from './alert-where';

export function useAlertWhereFacts(): AlertWhereFacts {
  const siteId = useSafeSiteId();
  const site = siteId ? (siteId as SiteId) : null;

  // The robots Fleet lists: this site's, and never the simulated fleet for
  // a site audience (`canSeeSimulated`) -- a robot the reader cannot see
  // there is not placed for them here either.
  const robots = useRobots().data;
  const showSimulated = useCanSeeSimulated();
  const visibleRobots = useMemo(
    () =>
      scopeByActiveSite(robots ?? [], siteId).filter(
        (r) => showSimulated || !isSimulatedFleet(r.fleet),
      ),
    [robots, siteId, showSimulated],
  );
  const lifts = useLifts().data;
  const presence = useRobotPresence(visibleRobots, lifts);
  const liftNames = useMemo(() => (lifts ?? []).map((l) => l.name), [lifts]);
  const liftViews = useLiftCarViews(liftNames, lifts);

  // A door's level. The registry is where it lives: a live door's state
  // row carries none. A row that does carry one (a registry-only row from
  // `useDoors`, or a live row once the state does) is read first.
  const doors = useDoors().data;
  const doorRegistry = useQuery(doorsRegistryQueryOptions(site)).data;
  const doorLevels = useMemo(() => {
    const levels = new Map<string, string>();
    for (const d of doorRegistry ?? []) if (d?.id?.door && d.level) levels.set(d.id.door, d.level);
    for (const d of doors ?? []) if (d.level) levels.set(d.name, d.level);
    return levels;
  }, [doors, doorRegistry]);

  const tasks = useTasks().data;
  const taskRobots = useMemo(() => {
    const byId = new Map<string, string>();
    for (const t of tasks ?? []) if (t.assignedRobot) byId.set(t.id, t.assignedRobot);
    return byId;
  }, [tasks]);

  return useMemo<AlertWhereFacts>(
    () => ({
      robotFloor(ref) {
        const r = splitRobotRef(ref);
        if (!r) return null;
        // A robot rule names the bare robot (`alerts_engine.py`). Two
        // fleets holding one name cannot be told apart from it, so such a
        // name is placed nowhere rather than at either robot's floor.
        const matches = visibleRobots.filter(
          (x) => x.name === r.robotName && (!r.fleetName || x.fleet === r.fleetName),
        );
        if (matches.length !== 1) return null;
        const robot = matches[0]!;
        return robotWhereFloor(presence.get(robotReceiptKey(robot.fleet, robot.name)));
      },
      liftFloor: (lift) => liftViews.get(lift)?.level ?? null,
      doorFloor: (door) => doorLevels.get(door) ?? null,
      taskRobot: (taskId) => taskRobots.get(taskId) ?? null,
    }),
    [visibleRobots, presence, liftViews, doorLevels, taskRobots],
  );
}
